/**
 * Biomes as weights, not classes.
 *
 * `biomeWeights` turns a climate into nine soft memberships that sum to one.
 * Terrain colour, tree species, scattered props, house styles and the sky all
 * blend by these weights, which is what keeps a transition a gradient: at the
 * edge of the Sahara a tile is part savanna and part desert, and everything in
 * it is mixed accordingly.
 */

import type { Climate } from './climate.js';
import { smoothstep } from './climate.js';

export const BIOMES = [
  'glacier', 'tundra', 'boreal', 'temperate', 'mediterranean',
  'steppe', 'desert', 'savanna', 'rainforest',
] as const;

export type Biome = (typeof BIOMES)[number];
export type BiomeWeights = Record<Biome, number>;

export function biomeWeights(c: Climate): BiomeWeights {
  const t = c.temperature;
  const frozen = Math.max(c.ice, 1 - smoothstep(-13, -7, t));
  const open = 1 - frozen;
  // Above ~7 °C the cold biomes give way; below -1 there are no trees.
  const tundra = open * (1 - smoothstep(-3, 0.5, t));
  const boreal = open * smoothstep(-3, 0.5, t) * (1 - smoothstep(3.5, 7, t));
  const mild = open * smoothstep(3.5, 7, t);

  const arid = smoothstep(0.56, 0.9, c.aridity);
  const semi = smoothstep(0.36, 0.52, c.aridity) * (1 - arid);
  const wet = 1 - arid - semi;
  const tropical = smoothstep(14.5, 19, t);

  const desert = mild * arid;
  const steppe = mild * semi * (1 - tropical);
  const rainforestShare = 1 - smoothstep(0.14, 0.32, c.aridity);
  const rainforest = mild * wet * tropical * rainforestShare;
  const savanna = mild * semi * tropical + mild * wet * tropical * (1 - rainforestShare);
  const mediterranean = mild * wet * (1 - tropical) * c.mediterranean;
  const temperate = mild * wet * (1 - tropical) * (1 - c.mediterranean);

  const weights: BiomeWeights = {
    glacier: frozen, tundra, boreal, temperate, mediterranean, steppe, desert, savanna, rainforest,
  };
  let total = 0;
  for (const b of BIOMES) total += weights[b];
  for (const b of BIOMES) weights[b] /= total > 0 ? total : 1;
  return weights;
}

export function dominantBiome(weights: BiomeWeights): Biome {
  let best: Biome = 'temperate';
  for (const b of BIOMES) if (weights[b] > weights[best]) best = b;
  return best;
}

/** Weighted sum of per-biome numbers. */
export function blend(weights: BiomeWeights, values: Record<Biome, number>): number {
  let sum = 0;
  for (const b of BIOMES) sum += weights[b] * values[b];
  return sum;
}

/**
 * How much snow lies on flat ground, 0..1, from the local temperature.
 * Separate from the biome weights because it follows elevation sharply — a
 * snow line on a mountain is a line, not a biome.
 */
export function snowCover(c: Climate): number {
  return Math.max(c.ice, 1 - smoothstep(-5, 1.5, c.temperature));
}

/**
 * The building tradition for a biome mix: whichever of five families carries
 * the most weight. Discrete on purpose — a village is built one way — and
 * chosen per tile, which is the scale a village occupies.
 */
export function houseRegion(w: BiomeWeights): 'temperate' | 'arid' | 'mediterranean' | 'tropical' | 'arctic' {
  const scores = {
    arctic: w.glacier + w.tundra,
    arid: w.desert + w.steppe * 0.45 + w.savanna * 0.2,
    mediterranean: w.mediterranean,
    tropical: w.rainforest + w.savanna * 0.8,
    temperate: w.temperate + w.boreal + w.steppe * 0.55,
  };
  let best: keyof typeof scores = 'temperate';
  for (const k of Object.keys(scores) as Array<keyof typeof scores>) if (scores[k] > scores[best]) best = k;
  return best;
}
